import type { AccumulatorScheduleLineResponse } from '@/store/targetAccumulator/api/tradeCapture/response.model.ts';
import type { TcValidation, Trade, TradeCaptureValidationObject } from '@/store/types.ts';
import type { BidAskPair, Frequency, PredealCheck } from '@/types/common.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';

export type TargetAccumulatorById = Record<string, FxTargetAccumulator>;

export type FxTargetAccumulatorProductName = FxTargetAccumulator['type'];

export type TcKey<T> = {
  [K in keyof T as `${K & string}String`]: T[K];
};

export type ScheduleError = Partial<{
  [TKey in keyof TcKey<AccumulatorScheduleLineResponse>]: TcValidation;
}>;

export type ScheduleErrors = Record<string, ScheduleError>;
export type TargetAccumulatorTradeCaptureError = TradeCaptureValidationObject<
  Omit<TargetAccumulatorProperties, 'schedule'>
> & {
  schedule: ScheduleErrors;
};

export type FxTargetAccumulator = Trade<
  'FxTargetAccumulator',
  TargetAccumulatorProperties,
  TargetAccumulatorTradeCaptureError
>;

export type TargetAccumulatorProperties = {
  isPriceable: boolean;
  tiering?: string;
  isEspDefaultTiering?: boolean;
  currencyPair?: CurrencyPair;
  clientsWay: ClientsWay;
  firstFixingDate?: ISODate;
  firstFixingTenor?: Tenor | null; // Mon / 3M / 90D
  lastFixingDate?: ISODate;
  lastFixingTenor?: Tenor | null; // Mon / 3M / 90D

  premiumDate: ISODate | null;
  premiumDateTenor: Tenor | null;
  premiumPriceType: AccumulatorPriceType;
  premiumTypeCurrencyIndex: CurrencyIndex;

  notional: number | null;
  notionalCurrencyIndex: CurrencyIndex;
  amountMode: AccumulatorAmountMode;

  barrier?: ResponseBarrier | null;

  hedgeType: HedgeType;
  possibleHedgeTypes: HedgeType[];
  spotHedge?: number;
  hedgeAmount: number | null;
  hedgeCurrencyIndex: CurrencyIndex;
  hedgePaymentDate: ISODate | null;

  marginCurrency: string;
  numberOfFixings?: number;
  numberOfSettlements?: number;

  leverage?: number;
  leverageAmount?: number;

  strike?: number;

  //PIVOT AND  EKI PIVOT
  pivot?: number;
  strikeDown?: number;
  strikeUp?: number;

  //EKI
  ekiTrigger?: number;

  //EKI PIVOT
  ekiDown?: number;
  ekiUp?: number;
  target?: number;

  scheduleIds?: string[];
  schedule?: Record<string, AccumulatorScheduleLine | null> | null;
  scheduleCustomFields?: ScheduleCustomFields;
  customFields?: CustomFields;

  totalLeverage?: number;
  totalNotional?: number;

  rfs: TargetAccumulatorRfs;
  rfsVersion: number;

  accuType?: TargetAccuType;
  accuTypeList?: TargetAccuType[];

  markupCurrency?: Currency;

  targetProfitType?: TargetProfitType;
  targetProfitTypeList?: TargetProfitType[];

  fixingFrequency?: Frequency;
  fixingFrequenciesList?: Frequency[];

  settlementFrequency?: Frequency;
  possibleSettlementFrequency?: Frequency; // used ?? not sure

  settlementMode: SettlementMode; // deliveryTypes
  settlementModeList: SettlementMode[]; // possibleDeliveryTypes;

  isCrossed?: boolean;
  crossCurrency?: string;
  crossCurrenciesList?: string[];

  fixingReference1?: string;
  fixingReference1Default?: string;
  fixingReference1List?: string[];
  fixingReference2?: string;
  fixingReference2Default?: string;
  fixingReference2List?: string[];

  summary: string;

  cashSettlementCurrency?: string;
  cashSettlementCurrencyList?: string[];

  maxDate?: string;

  mifid: TargetAccumulatorMifid;

  // Here for MP changes
  calendar?: AccumulatorCalendarLine[];
  expiryDate?: ISODate | null;
  expiryDateTenor?: Tenor | null;
  deliveryDate?: ISODate | null;
  cutOffMarketPlace?: string;
  strikeComparator?: string;

  uiState: {
    displayMode: DisplayMode;
  };
};

export type TargetProfitType = 'ADJUSTED_BY_AMOUNT' | 'ADJUSTED_BY_STRIKE' | 'FULL' | 'NONE';
export const DEFAULT_HEDGE_TYPE = 'Live';
export const POSSIBLE_HEDGE_TYPE = ['Live', 'Spot'] as const;
export type HedgeType = (typeof POSSIBLE_HEDGE_TYPE)[number];

export const DEFAULT_CLIENTS_WAY = 'Buy';
export const POSSIBLE_CLIENTS_WAY = ['Buy', 'Sell'] as const;
export type ClientsWay = (typeof POSSIBLE_CLIENTS_WAY)[number];

export const DEFAULT_PRICE_TYPE = 'Amount';
export const POSSIBLE_PRICE_TYPE = ['Amount'] as const;
export type AccumulatorPriceType = (typeof POSSIBLE_PRICE_TYPE)[number];

export type PossibleProperty<T = string> = {
  default: T;
  possible: T[];
};

export const DEFAULT_TARGET_ACCU_TYPE = 'TargetAccu';
export const POSSIBLE_TARGET_ACCU_TYPE = ['TargetAccu', 'PIVOTEKI', 'PIVOT', 'EKI'] as const;
export type TargetAccuType = (typeof POSSIBLE_TARGET_ACCU_TYPE)[number];

export type AccumulatorCalendarLine = CommonScheduleCalendarData & {
  stepDown: number | null;
  stepUp: number | null;
  step: number | null;
  amountDown: number | null;
  amountUp: number | null;
  fixingValue: number | null;
  weight: number | null;
  isDelivered: boolean | null;
  amountMid: number | null;
  power: number | null;
  leverage: number | null;
  targetGapDeltaProfit: number | null;
  couponGapDelta: number | null;
};

export type AccumulatorScheduleLine = CommonScheduleCalendarData & {
  pivot: number | null;
  ekiTrigger: number | null;
  ekiUp: number | null;
  ekiDown: number | null;
  strikeDown: number | null;
  strikeUp: number | null;
  amount: number | null;
  leverageAmount: number | null;
};

export type CommonScheduleCalendarData = {
  fixingDate: string | null;
  paymentDate: string | null;
  strike: number | null;
};

export type TargetAccumulatorRfsStatus =
  | 'NOT_PRICEABLE'
  | 'PRICEABLE'
  | 'AWAITING_INDICATIVE'
  | 'AWAITING_EXECUTABLE'
  | 'STREAMING_INDICATIVE'
  | 'STREAMING_EXECUTABLE'
  | 'ERROR'
  | 'BYPASSABLE_ERROR';

export type TargetAccumulatorRfs =
  | { status: 'AWAITING'; rfsId: string; quoteType: 'Indicative' | 'Tradeable'; traderId?: string }
  | {
      status: 'PRICING';
      rfsId: string;
      quote: TargetAccumulatorQuote;
      quoteId: string;
      quoteType: 'Indicative' | 'Tradeable';
      traderId?: string;
      initialRfsWindow: number;
    }
  | { status: 'EXPIRED'; quote: TargetAccumulatorQuote }
  | { status: 'CANCELLED' }
  | { status: 'ERROR'; rfsId?: string; error: StreamingError };

type StreamingError = {
  multipassErrorCode: string;
  errorCode: string;
  errorMessage: string;
};

export type LegSide = 'Buy' | 'Sell';
export type Side = LegSide | 'Both';

export const accumulatorAmountModes = ['Fixing', 'Total'] as const;
export type AccumulatorAmountMode = (typeof accumulatorAmountModes)[number];

export const settlementModes = ['Cash', 'Physical_Spot', 'Physical_Forward'] as const;
export type SettlementMode = (typeof settlementModes)[number];

export type ResponseBarrier =
  | {
      level: number;
      levelString: string;
      type: string;
      nature: 'American';
    }
  | {
      level: number;
      levelString: string;
      type: undefined;
      nature: undefined;
    }
  | {
      level: undefined;
      levelString: undefined;
      type: string;
      nature: undefined;
    };

export type ScheduleCustomFields = Record<
  string,
  | Array<
      | 'FixingDate'
      | 'PaymentDate'
      | 'Strike'
      | 'Pivot'
      | 'Amount'
      | 'LeverageAmount'
      | 'EkiTrigger'
      | 'EkiUp'
      | 'EkiDown'
      | 'StrikeDown'
      | 'StrikeUp'
    >
  | undefined
>;

export type CustomFields = Array<
  | 'SettlementFrequency'
  | 'FixingFrequency'
  | 'Strike'
  | 'Pivot'
  | 'EkiTrigger'
  | 'Amount'
  | 'LeverageAmount'
  | 'StrikeDown'
  | 'StrikeUp'
  | 'EkiDown'
  | 'EkiUp'
>;

export type TargetAccumulatorQuote = {
  quoteId: string;
  quoteType: QuoteType;
  rfsWindow: number;
  hedge?: HedgeData;
  predealChecks: PredealCheck[];
  marginData: TargetAccumulatorRfsMarginDataState;
  quoteContribution: 'Automatic' | 'Manual';

  tiering: string | null;
  premiumData: TargetAccumulatorRfsPremiumDataState;
  greeks: TargetAccumulatorRfsGreeksState;
  isEligibleToPremiumRateStoMid: boolean;
  spot: BidAskPair;
  forward: BidAskPair;
  swapPoints: BidAskPair;
  volatility: number;
  frontTimestamp: Timestamp;
  backTimestamp: number;
};

export type TargetAccumulatorRfsGreeksState = {
  deltaCcy1: number;
  deltaCcy2: number;
  vegaCcy1: number;
  vegaCcy2: number;
};

export type TargetAccumulatorRfsPremiumDataState = {
  premiumCcy1: BidAskPair;
  premiumCcy2: BidAskPair;
  premiumPctCcy1: number;
  premiumPctCcy2: number;
  premiumRateStoCcy1Mid: number;
  premiumRateStoCcy2Mid: number;
  premiumGappedCcy1Mid: number;
  premiumGappedCcy2Mid: number;
};

export type TargetAccumulatorRfsMarginDataState = {
  markupCcy1: number;
  markupCcy2: number;
};

export interface HedgeData {
  maturityDate: string | undefined;
  rateMid: string | undefined;
  notionalAmount: string | undefined;
  notionalCurrency: string | undefined;
}

export type QuoteType = 'Tradeable' | 'Indicative';

interface TargetAccumulatorMifid {
  rfsDate?: string;
  records: MifidRecord[];
}

interface MifidRecord {
  date: string;
  isError?: boolean;
}

export type DisplayMode = 'Pricer' | 'Scheduler';
